import { Notice } from "@/components/Notice";
import { ClasswizeModal } from "@/components/ui/Modal";
import { useSubmitFeedbackMutation } from "@/query/feedbackQuery";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { Box, Button, Link, ModalBody, ModalFooter, Text, Textarea } from "@chakra-ui/react";
import React, { Fragment, useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import userflow from "userflow.js";
import shallow from "zustand/shallow";

interface Props {
	handleClose: () => void;
	classroomName: string;
}

const FeedbackModal: React.FC<Props> = ({ classroomName, handleClose }) => {
	const { t } = useTranslation();
	const [currentMessage, setCurrentMessage] = useState<string>("");
	const [formError, setFormError] = useState<string>("");

	const selectedClassroom = useSelectedClassroomStore();
	const userSession = useUserSessionStore(
		(state) => ({
			applianceId: state.deviceid,
			username: state.active_username,
		}),
		shallow
	);

	const submitFeedbackMutation = useSubmitFeedbackMutation({
		onError: (err) => {
			console.error("Failed to submit feedback: ", err);
		},
	});

	const handleMessageChange = (evt: { target: { value: string } }) => {
		setCurrentMessage(evt.target.value);
	};

	const handleSubmit = () => {
		if (currentMessage === "") {
			setFormError(t("feedbackModal.emptyMessage"));
			return;
		}

		if (!classroomName || !selectedClassroom.stableIdV1 || !selectedClassroom.id) {
			setFormError(t("feedbackModal.processError"));
			console.error("Failed to submit feedback: Missing classroom details");
			return;
		}

		if (!userSession.applianceId || !userSession.username) {
			setFormError(t("feedbackModal.processError"));
			console.error("Failed to submit feedback: Missing user session details", {
				applianceId: userSession.applianceId,
				username: userSession.username,
			});
			return;
		}

		setFormError("");

		submitFeedbackMutation.mutate({
			message: currentMessage,
			classroomName: classroomName,
			classroomStableId: selectedClassroom.stableIdV1,
			classroomId: selectedClassroom.id,
			applianceId: userSession.applianceId,
			teacherUsername: userSession.username,
		});
	};

	const openHelpCenter = useCallback(() => {
		userflow.openResourceCenter();
	}, []);

	const errorMessage = formError || (submitFeedbackMutation.isError ? t("feedbackModal.submitError") : "");

	return (
		<ClasswizeModal
			isOpen
			onClose={handleClose}
			size="3xl"
			dataTestId="feedback-modal"
			title={submitFeedbackMutation.isSuccess ? t("feedbackModal.sentTitle") : t("feedbackModal.title")}
		>
			<ModalBody display="flex" alignItems="center" flexDirection="column">
				{submitFeedbackMutation.isSuccess ? (
					<Fragment>
						<Text alignSelf="flex-start">{t("feedbackModal.thanks")}</Text>
						<Button alignSelf="flex-end" onClick={handleClose}>
							{t("account.close")}
						</Button>
					</Fragment>
				) : (
					<Fragment>
						<Box mb="16px">
							<Text fontFamily="paragraphs" fontSize="16px" color="text.paragraph.regular" textAlign="center">
								{t("feedbackModal.intro")}
							</Text>
						</Box>
						<Notice
							status="info"
							mb={4}
							children={
								<Box data-testid="FeedbackModalSupportMessage" textAlign="left">
									<Text mb={2}>{t("feedbackModal.supportNoticeFormPurpose")}</Text>
									<Text mb={2}>
										<Trans
											i18nKey="feedbackModal.supportNoticeHelp"
											components={{
												inAppHelpCenter: <Link as="button" type="button" onClick={openHelpCenter} />,
												externalHelpCenter: <Link href="https://help.classwize.net" target="_blank" rel="noopener noreferrer" />,
											}}
										/>
									</Text>
									<Text>{t("feedbackModal.supportNoticeTechnical")}</Text>
								</Box>
							}
						/>

						<Textarea
							data-testid="FeedbackModalTextArea"
							sx={{ height: "150px", minHeight: "50px" }}
							name="messageBody"
							onChange={handleMessageChange}
							value={currentMessage}
							placeholder={t("feedbackModal.placeholder")}
						/>
						<Text className="error">{errorMessage}</Text>
					</Fragment>
				)}
			</ModalBody>
			{!submitFeedbackMutation.isSuccess && (
				<ModalFooter>
					<Button onClick={handleSubmit} isDisabled={submitFeedbackMutation.isLoading} data-testid="submit-feedback-button">
						{t("feedbackModal.submitButton")}
					</Button>
				</ModalFooter>
			)}
		</ClasswizeModal>
	);
};

export default FeedbackModal;
